import { useState, useEffect } from 'react';
import { fromLonLat } from 'ol/proj';
import { Button, Input, Stack, Text } from '@chakra-ui/react';
import { coordinateService } from '../../lib/services/coordinateService';
import { useMap } from '../map/map-context';
import { storageManager } from '../../lib/utils/storage-manager';

export default function LatLongSearch({ onClose }: { onClose: () => void }) {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { map } = useMap();

  // Load saved coordinates on component mount
  useEffect(() => {
    const savedState = storageManager.loadLatLongState();
    if (savedState.latitude) {
      setLatitude(savedState.latitude);
    }
    if (savedState.longitude) {
      setLongitude(savedState.longitude);
    }
  }, []);

  const validateCoordinates = (lat: number, lng: number): boolean => {
    return lat >= 49 && lat <= 60 && lng >= -110 && lng <= -101;
  };

  const handleSearch = async () => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      setError('Please enter valid numeric coordinates');
      return;
    }

    if (!validateCoordinates(lat, lng)) {
      setError('Coordinates must be within Saskatchewan bounds');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await coordinateService.checkCoordinate(lat, lng);

      // Save coordinates to storage
      storageManager.saveLatLongState(latitude, longitude);

      if (map) {
        const view = map.getView();
        view.animate({
          center: fromLonLat([lng, lat]),
          zoom: 15,
          duration: 1000,
        });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate coordinates');
    } finally {
      setLoading(false);
    }
  };

  // Save coordinates when they change
  const handleLatitudeChange = (value: string) => {
    setLatitude(value);
    if (value) {
      storageManager.saveLatitude(value);
    }
  };

  const handleLongitudeChange = (value: string) => {
    setLongitude(value);
    if (value) {
      storageManager.saveLongitude(value);
    }
  };

  return (
    <Stack gap='4'>
      <Text fontSize='sm' color='gray.600'>
        Enter coordinates in decimal degrees
      </Text>

      <Stack gap='3'>
        <div>
          <Text fontSize='sm' mb='1'>
            Latitude
          </Text>
          <Input
            type='number'
            step='any'
            placeholder='e.g. 52.1579'
            value={latitude}
            onChange={(e) => handleLatitudeChange(e.target.value)}
          />
        </div>

        <div>
          <Text fontSize='sm' mb='1'>
            Longitude
          </Text>
          <Input
            type='number'
            step='any'
            placeholder='e.g. -106.6702'
            value={longitude}
            onChange={(e) => handleLongitudeChange(e.target.value)}
          />
        </div>
      </Stack>

      {error && (
        <Text color='red.500' fontSize='sm'>
          {error}
        </Text>
      )}

      <Button onClick={handleSearch} loading={loading} disabled={!latitude || !longitude}>
        Search Coordinates
      </Button>
    </Stack>
  );
}
